import { useEffect, useState } from 'react'
import { ICustomer } from '../Appointment/store/interface/IAppointmentState'
import { getCustomerSessionToken } from '../cookies/session.cookies'
import { getPricePreview } from './client'
import { PricePreviewResponse } from './types'

export type ActivityPriceInfo = {
  activityId: number
  categoryId: number | null
  price: number
  deposit: number
  punchCardUuid: string | null
}

function buildPriceInfo(response: PricePreviewResponse): ActivityPriceInfo[] {
  const result: ActivityPriceInfo[] = []

  for (const customer of response._resource.customers) {
    for (const activity of customer.price_breakdown.activities) {
      result.push({
        activityId: activity.activity_id,
        categoryId: null,
        price: activity.price,
        deposit: activity.deposit,
        punchCardUuid: activity.punch_card_customer_uuid,
      })
    }
    for (const menu of customer.price_breakdown.menus) {
      result.push({
        activityId: menu.activity_ids[0],
        categoryId: menu.category_id,
        price: menu.price,
        deposit: menu.deposit,
        punchCardUuid: menu.punch_card_customer_uuid,
      })
    }
  }

  return result
}

export function getPunchCardUuids(priceInfo: ActivityPriceInfo[]): string[] {
  return [...new Set(
    priceInfo
      .map((info) => info.punchCardUuid)
      .filter((uuid): uuid is string => uuid != null),
  )]
}

type UsePricePreviewResult = {
  priceInfo: ActivityPriceInfo[]
  punchCardUuids: string[]
  totalPrice: number
  totalDeposit: number
  loading: boolean
}

export function usePricePreview(
  establishmentUuid: string,
  establishmentId: string,
  customers: ICustomer[],
  appointmentDate?: string,
  explicitPunchCardUuids?: string[],
): UsePricePreviewResult {
  const [priceInfo, setPriceInfo] = useState<ActivityPriceInfo[]>([])
  const [totalPrice, setTotalPrice] = useState(0)
  const [totalDeposit, setTotalDeposit] = useState(0)
  const [loading, setLoading] = useState(false)

  const token = getCustomerSessionToken(establishmentId)
  const hasActivities = customers.some((c) => c.activities?.length > 0)

  useEffect(() => {
    if (!hasActivities) {
      setPriceInfo([])
      setTotalPrice(0)
      setTotalDeposit(0)
      return
    }

    const body = {
      ...(appointmentDate ? { appointment_date: appointmentDate } : {}),
      customers: customers.filter((c) => c.activities?.length > 0).map((customer) => ({
        token,
        punch_card_customer_uuids: explicitPunchCardUuids ?? [],
        blocks: (customer.activities ?? []).map((activity) => ({
          activity_id: activity.id,
          category_id: activity.categoryId ?? null,
          person_id: activity.personSelected?.id ?? null,
        })),
      })),
    }

    setLoading(true)
    getPricePreview(establishmentUuid, body)
      .then((response) => {
        const info = buildPriceInfo(response)
        setPriceInfo(info)
        setTotalPrice(response._resource.full_amount)
        setTotalDeposit(response._resource.deposit_amount)
      })
      .catch(() => {
        setPriceInfo([])
        setTotalPrice(0)
        setTotalDeposit(0)
      })
      .finally(() => setLoading(false))
  }, [
    hasActivities,
    token,
    appointmentDate,
    explicitPunchCardUuids?.join(','),
    customers.map((c) => c.activities?.map((a) => `${a.id}-${a.personSelected?.id}`).join(',')).join(';'),
  ])

  return { priceInfo, punchCardUuids: getPunchCardUuids(priceInfo), totalPrice, totalDeposit, loading }
}
